import { useEffect } from 'react';
import { useAppDispatch } from '@store/hooks';
import { setUserList } from '@store/user/user.slice';
import { useTranslation } from 'react-i18next';
import { captureWarning } from '@utils/sentry';
import { setBigVideoAndDisplayModeFromUserList } from '@store/studio/studio.slice';
import wemeetClient from '../wemeetClient';
import { LocalStream, RemoteStream } from '@tencent/wemeet-websdk-next';
import { openDialog } from '@component/dialog';
import { PLAY_NOT_ALLOWED, VideoPrefix } from '@constant/media';
import { muteAudio, muteVideo } from '@store/media/media.thunk';
import ISSUE_NAME from '@constant/issue-name';
import { setLoadingMapValue } from '@utils/helper/media';

const streamIdPlayingMap: any = {};
const errorStreamIdMap: any = {};

export const useStreamListener = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const addAudio = (stream: LocalStream | RemoteStream, streamId: string) => {
    const audio = document.createElement('audio');
    audio.id = streamId;
    const parent = document.getElementById('audio-wrapper');
    parent!.appendChild(audio);
  };

  const playStream = (stream: LocalStream | RemoteStream, streamId: string, isLocal: boolean) => {
    if (!streamIdPlayingMap[streamId]) {
      addAudio(stream, streamId);
      stream
        .play(streamId, {
          muted: !!isLocal,
        })
        .catch((err: any) => {
          console.log(`流play错误`, err);
        });
      streamIdPlayingMap[streamId] = true;
    }
  };

  /** @description 更新视频信息 */
  const updateVideoElementInfo = (stream: LocalStream | RemoteStream, attr: 'play' | 'srcObject') => {
    const streamId = stream.getId();
    Object.values(VideoPrefix).forEach((prefix) => {
      const id = `${prefix}-${streamId}`;
      const videoEl = document.getElementById(id);

      if (videoEl) {
        if (attr === 'srcObject') {
          // @ts-ignore
          (videoEl as any).srcObject = stream.mediaStream_;
        } else if (attr === 'play') {
          (videoEl as any).play();
        }
      }
    });
  };

  const watchRemoteStream = (stream: RemoteStream, streamId: string) => {
    // @ts-ignore
    stream.on('error', (error: any) => {
      const errorCode = error.getCode();
      console.log('远程流监听到错误', error, errorCode);
      // https://trtc-1252463788.file.myqcloud.com/web/docs/tutorial-11-advanced-auto-play-policy.html

      if (errorCode === PLAY_NOT_ALLOWED) {
        errorStreamIdMap[streamId] = stream;
        // PLAY_NOT_ALLOWED,引导用户手势操作并调用 stream 恢复音视频播放
        // FIXME 2021.4.7 如果已经被踢出了，则不弹出, 当前没有记录踢出状态
        openDialog({
          dialogId: 'bowerAllowAudio',
          title: t('因为浏览器限制，需要您确认后才可以播放会中的声音'),
          onOk: () => {
            Object.keys(errorStreamIdMap).forEach((key) => {
              if (errorStreamIdMap[key]) {
                // 由于互踢逻辑，可能web加完之后，那个远程的流已经不见了，调resume会报错，做一下兼容
                errorStreamIdMap[key].resume();
                // 需要对原生video调用play方法才能恢复播放
                updateVideoElementInfo(errorStreamIdMap[key], 'play');
              }
            });
          },
        });
      }
    });
  };

  const watchLocalStream = (stream: LocalStream) => {
    stream.on('player-state-changed', (event: any) => {
      const { state, type } = event;
      console.log('state:', state);
      // important 本地流结束的回调不会进入到下面
      // 2021.2.5  假如本地流被end，证明视频采集不正常，应该调用xmpp 关闭视频上行的方法，然后提示用户视频采集出错，请重试
      // @ts-ignore
      if (state === 'STOPPED' && type === 'video' && !stream.getScreen()) {
        openDialog({
          title: t('错误'),
          dialogId: 'camera-error',
          content: t('摄像头出错，请检查后重新开启'),
          okText: t('我知道了'),
        });
        // 老逻辑，摄像头出错之后就关了
        dispatch(muteVideo());
      }
      // @ts-ignore
      if (state === 'STOPPED' && type === 'audio' && !stream.getScreen()) {
        openDialog({
          dialogId: 'mic-error',
          title: t('错误'),
          content: t('麦克风出错，请检查后重新开启'),
          okText: t('我知道了'),
        });
        // 老逻辑 麦克风stop了之后静音
        dispatch(muteAudio());
      }
      // 本地共享流断开
      // 本地共享流因为窗口被关闭，选项卡被关闭而断开
      // @ts-ignore
      if (state === 'STOPPED' && type === 'video' && stream.getScreen()) {
        openDialog({
          title: t('屏幕共享已结束'),
          content: t('由于共享的窗口被关闭或其他原因，屏幕共享结束。'),
          onOk: () => {
            // TODO 旧版本this.doRtcScreenShare(false);
          },
        });
      }
    });
    // @ts-ignore
    stream.on('error', (error: any) => {
      const errorCode = error.getCode();
      console.log('本地流监听到错误', error, errorCode);
    });
  };

  /**
   *
   * 流增加的处理
   * 1. addAudio 新增一个音频播放器
   * 2. 播放音频 （这个流是视频流/音频流都只播放声音）
   * 3. 监听流
   * @param stream
   * @param isLocal
   */
  const handleAddStream = (stream: LocalStream | RemoteStream, isLocal: boolean) => {
    const streamId = stream.getId();
    playStream(stream, streamId, isLocal);
    if (isLocal) {
      watchLocalStream(stream as LocalStream);
    } else {
      watchRemoteStream(stream as RemoteStream, streamId);
    }
  };

  const remoteAudio = (stream: LocalStream | RemoteStream) => {
    const streamId = stream.getId();
    const audio = document.getElementById(streamId);
    const parent = document.getElementById('audio-wrapper');
    if (audio) {
      parent!.removeChild(audio);
    }
  };
  useEffect(() => {
    const streamAddEvent = wemeetClient.onStreamAdd((stream: LocalStream | RemoteStream, isLocal: boolean) => {
      console.log('===流增加===:', stream);
      const userList = wemeetClient.userCtrl.getUserInfoList();
      console.log('增加之后的userList:', [...userList]);
      handleAddStream(stream, isLocal);

      // FIXME:
      // 这里其实本不需要更新userList，SDK会统一触发userUpdate，但是为了防止userUpdate下发延迟，所以多处理一次
      dispatch(setUserList(userList));
      dispatch(setBigVideoAndDisplayModeFromUserList(userList));
    });

    const streamRemoveEvent = wemeetClient.onStreamRemove((stream) => {
      console.log('===流移除===：', stream);
      remoteAudio(stream);
      const streamId = stream.getId();
      errorStreamIdMap[streamId] = null;
      streamIdPlayingMap[streamId] = null;
      setLoadingMapValue(streamId, false);
      const userList = wemeetClient.userCtrl.getUserInfoList();
      console.log('移除后的userList:', userList);
      dispatch(setUserList(userList));
      dispatch(setBigVideoAndDisplayModeFromUserList(userList));
    });

    const streamUpdateEvent = wemeetClient.onStreamUpdate((stream) => {
      console.log('===流更新===：', stream);
      // @ts-ignore
      if (stream.mediaStream_ === null) {
        captureWarning(ISSUE_NAME.STREAM_NO_MEDIA, 'streamListen', {
          data: '收听到 streamUpdatedHandler ${stream.getUserId()}流里面没有媒体数据',
        });
        return;
      }

      const userList = wemeetClient.userCtrl.getUserInfoList();
      // 同步更新userList 里的流媒体数据
      dispatch(setUserList(userList));
      // 老版本还有这个操作，用于更新播放的媒体流，我的理解上面的就够了
      // updateVideoElementInfo(stream, 'srcObject');
    });
    return () => {
      wemeetClient.removeListener(streamAddEvent);
      wemeetClient.removeListener(streamRemoveEvent);
      wemeetClient.removeListener(streamUpdateEvent);
    };
  }, []);
};
